// src/components/Registros/forms/IncapacidadForm.jsx
import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import SelectMult from '../../SettingNot/SelectMult';
import { useNavigate } from 'react-router-dom';
import AdjuntosRegistro from '../AdjuntosRegistro';
import { crearDatos } from '../../../api/common';

const IncapacidadForm = ({ onClose, onSubmit, tipoRegistro, solicitante }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    solicitante: solicitante,
    titulo: '',
    tipoIncapacidad: '',
    fechaInicio: '',
    fechaFin: '',
    eps: '',
    numeroIncapacidad: '',
    descripcion: '',
    responsablesAprobacion: [],
    adjuntos: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const responsables = [
    { id: '1', nombre: 'Juan Pérez', cargo: 'Jefe de RRHH' },
    { id: '2', nombre: 'María García', cargo: 'Coordinador de Nómina' }
  ];

  const tiposIncapacidad = [
    { value: 'enfermedad_general', label: 'Enfermedad General' },
    { value: 'accidente_trabajo', label: 'Accidente de Trabajo' },
    { value: 'enfermedad_laboral', label: 'Enfermedad Laboral' },
    { value: 'licencia_maternidad', label: 'Licencia de Maternidad' },
    { value: 'licencia_paternidad', label: 'Licencia de Paternidad' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const calcularDias = () => {
    if (formData.fechaInicio && formData.fechaFin) {
      const inicio = new Date(formData.fechaInicio);
      const fin = new Date(formData.fechaFin);
      const diffTime = Math.abs(fin - inicio);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const handleResponsableChange = (e) => {
    const { value } = e.target; // value es un array de IDs: ['id1', 'id2', ...]
    
    // Transformar el array de IDs a objetos con la estructura del modelo
    const responsablesFormateados = value?.map((usuarioId, index) => ({
      usuario: usuarioId,
      orden: index + 1,
      estado: 'pendiente'
    }));
    setFormData(prev => ({
      ...prev,
      responsablesAprobacion: responsablesFormateados
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Título se autogenera a partir del tipoIncapacidad (ver handleSubmit).

    if (!formData.tipoIncapacidad) {
      newErrors.tipoIncapacidad = 'Debe seleccionar el tipo de incapacidad';
    }

    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es requerida';
    }

    if (!formData.fechaFin) {
      newErrors.fechaFin = 'La fecha de fin es requerida';
    }

    if (formData.fechaInicio && formData.fechaFin) {
      if (new Date(formData.fechaFin) < new Date(formData.fechaInicio)) {
        newErrors.fechaFin = 'La fecha fin debe ser mayor o igual a la fecha inicio';
      }
    }

    if (!formData.eps.trim()) {
      newErrors.eps = 'La EPS es requerida';
    }

    if (!formData.numeroIncapacidad.trim()) {
      newErrors.numeroIncapacidad = 'El número de incapacidad es requerido';
    }

    if (formData.responsablesAprobacion.length === 0) {
      newErrors.responsablesAprobacion = 'Debe seleccionar al menos un responsable';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Errores en el formulario',
        text: 'Por favor, corrija los errores antes de continuar',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    setLoading(true);

    try {
      const tipoLabel = (formData.tipoIncapacidad || '').replace(/_/g, ' ').trim();
      const tituloAuto = tipoLabel ? `Incapacidad (${tipoLabel})` : 'Incapacidad';
      const dataToSubmit = {
        ...formData,
        titulo: tituloAuto,
        tipoSolicitud: 'incapacidad',
        causaInactividad: true
      };

      await crearDatos('Registro', dataToSubmit);

      await Swal.fire({
        icon: 'success',
        title: 'Incapacidad Registrada',
        text: 'Tu incapacidad ha sido registrada correctamente',
        confirmButtonColor: '#198754'
      });
      if (onSubmit) onSubmit(dataToSubmit);
      navigate('/Solicitudes');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al registrar la incapacidad. Por favor, intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Los datos ingresados se perderán',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, continuar'
    }).then((result) => {
      if (result.isConfirmed) {
        onClose();
      }
    });
  };

  return (
    <Container fluid className="py-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <div className="form-container">
        <div className="form-header" style={{ background: 'linear-gradient(135deg, #dc3545 0%, #b02a37 100%)' }}>
          <div className="d-flex align-items-center">
            <Button variant="light" size="sm" onClick={handleCancel} className="me-3">
              <FaArrowLeft />
            </Button>
            <div>
              <h3 className="mb-0 fw-bold">Registro de Incapacidad</h3>
              <p className="mb-0 opacity-75">Complete el formulario para reportar una incapacidad médica</p>
            </div>
          </div>
        </div>

        <div className="form-body">
          <Alert variant="warning" className="d-flex align-items-center">
            <FaExclamationTriangle className="me-2" size={20} />
            <div>
              <strong>Importante:</strong> Esta incapacidad causará inactividad durante el periodo indicado. 
              Recuerda adjuntar el documento de incapacidad expedido por tu EPS.
            </div>
          </Alert>

          <Form onSubmit={handleSubmit}>
            {/* Información Básica */}
            <div className="form-section">
              <h5 className="form-section-title">Información Básica</h5>
              
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold" style={{display:'none'}}>Título (auto)</Form.Label>
                    <Form.Control
                      type="hidden"
                      name="titulo"
                      value={formData.titulo}
                      onChange={handleChange}
                      placeholder="Ej: Incapacidad por gripe"
                      isInvalid={!!errors.titulo}
                      maxLength={100}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.titulo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Tipo de Incapacidad *</Form.Label>
                    <Form.Select
                      name="tipoIncapacidad"
                      value={formData.tipoIncapacidad}
                      onChange={handleChange}
                      isInvalid={!!errors.tipoIncapacidad}
                    >
                      <option value="">Seleccione un tipo</option>
                      {tiposIncapacidad.map(tipo => (
                        <option key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.tipoIncapacidad}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Información Médica */}
            <div className="form-section">
              <h5 className="form-section-title">Información Médica</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">EPS *</Form.Label>
                    <Form.Control
                      type="text"
                      name="eps"
                      value={formData.eps}
                      onChange={handleChange}
                      placeholder="Nombre de su EPS"
                      isInvalid={!!errors.eps}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.eps}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Número de Incapacidad *</Form.Label>
                    <Form.Control
                      type="text"
                      name="numeroIncapacidad"
                      value={formData.numeroIncapacidad}
                      onChange={handleChange}
                      placeholder="Número del documento"
                      isInvalid={!!errors.numeroIncapacidad}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.numeroIncapacidad}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Periodo de Incapacidad */}
            <div className="form-section">
              <h5 className="form-section-title">Periodo de Incapacidad</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha de Inicio *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaInicio"
                      value={formData.fechaInicio}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaInicio}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaInicio}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha de Fin *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaFin"
                      value={formData.fechaFin}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaFin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaFin}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {calcularDias() > 0 && (
                <Alert variant="info">
                  Duración de la incapacidad: <strong>{calcularDias()}</strong> día(s)
                </Alert>
              )}
            </div>

            {/* Descripción */}
            <div className="form-section">
              <h5 className="form-section-title">Descripción</h5>
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Información adicional sobre la incapacidad (opcional)..."
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {formData.descripcion.length}/500 caracteres
                </Form.Text>
              </Form.Group>
            </div>

            {/* Responsables */}
            <div className="form-section">
              <h5 className="form-section-title">Responsables de Aprobación</h5>
              
              <Form.Group>
                <Form.Label className="fw-bold">Seleccione los responsables que deben aprobar *</Form.Label>
                <SelectMult
                //dataDefault={itemData?.userAutorizados}
                handledata={handleResponsableChange}
                name='responsablesAprobacion'
                collection={"Usuarios"}
                field={"nombre"}
                baseFilter={[ // Body para la consuta aggregate de mongo para metrologos y que esten activos.
                        {
                            "$match": {
                            'status': 'activo'
                            } 
                        },
                        {
                            "$lookup": {
                            "from": "roles",
                            "localField": "role",
                            "foreignField": "_id",
                            "as": "result"
                            }
                        },
                        {
                            "$match": {
                            "result.nombre": { $ne: "Cliente" } 
                            }
                        }
                        ]}
                />
                {errors.responsablesAprobacion && (
                  <Alert variant="danger" className="mt-2 py-2">
                    {errors.responsablesAprobacion}
                  </Alert>
                )}
              </Form.Group>
            </div>

            <div className="form-section">
              <h5 className="form-section-title">Adjuntos</h5>
              <AdjuntosRegistro
                tipoSolicitud="incapacidad"
                adjuntos={formData.adjuntos}
                onChange={(arr) => setFormData(prev => ({ ...prev, adjuntos: arr }))}
                disabled={loading}
              />
            </div>

            {/* Botones */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                variant="secondary" 
                onClick={handleCancel}
                disabled={loading}
                className="px-4"
              >
                <FaTimes className="me-2" />
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
                className="px-4"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    Registrar Incapacidad
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default IncapacidadForm